// Angle Types and Basics — rich slide renderer.
//
// Each slide is a self-contained component that reads `fragmentIndex`
// (0-based) and renders content + SVG state accordingly. The dispatcher
// at the bottom maps slide.id → component.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InlineMath, BlockMath } from "../Math";
import { X } from "lucide-react";
import "katex/dist/katex.min.css";

/* ============ Concept-check / engagement popup ============
   Pushed by the teacher's mobile remote. Renders ONLY when
   `activeQuestion` is non-null in the synced session state.
*/
const QuestionPopup = ({ activeQuestion, slideId, fragmentIndex }) => {
    const suppressForRemote = React.useMemo(() => {
        try {
            return (
                new URLSearchParams(window.location.search).get("embed") ===
                "remote"
            );
        } catch {
            return false;
        }
    }, []);

    const visible =
        !!activeQuestion?.text &&
        !suppressForRemote &&
        (activeQuestion.slideId == null ||
            activeQuestion.slideId === slideId) &&
        (activeQuestion.fragmentIndex == null ||
            activeQuestion.fragmentIndex === fragmentIndex);

    const kind = activeQuestion?.kind || "concept-check";

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="absolute inset-0 z-40 flex items-center justify-center px-24"
                    style={{
                        background: "rgba(8,16,24,0.55)",
                        backdropFilter: "blur(14px) saturate(140%)",
                    }}
                    data-testid={
                        kind === "concept-check"
                            ? "concept-check-popup"
                            : "engagement-popup"
                    }
                >
                    <motion.div
                        initial={{ scale: 0.94, y: 24 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.96, y: 12 }}
                        transition={{
                            duration: 0.5,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        style={{
                            background: "white",
                            borderRadius: 28,
                            maxWidth: 820,
                            width: "100%",
                            padding: "40px 52px",
                            boxShadow:
                                "0 60px 160px rgba(8,16,24,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
                            position: "relative",
                        }}
                    >
                        <div
                            style={{
                                display: "inline-block",
                                background:
                                    kind === "concept-check"
                                        ? "linear-gradient(135deg, #5BC2CC, #2E8E9A)"
                                        : "linear-gradient(135deg, #FFA987, #C75730)",
                                color: "white",
                                padding: "6px 14px",
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                marginBottom: 18,
                            }}
                        >
                            {kind === "concept-check"
                                ? "Concept check"
                                : "Think with me"}
                        </div>
                        <p
                            style={{
                                fontSize: 26,
                                lineHeight: 1.3,
                                color: "#14202a",
                                fontWeight: 600,
                                letterSpacing: "-0.005em",
                            }}
                        >
                            {activeQuestion.text}
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/* ============ shared helpers ============ */
const Reveal = ({ showAt, fragmentIndex, children, className = "", from = "y" }) => {
    const initial =
        from === "x"
            ? { opacity: 0, x: -24 }
            : from === "scale"
              ? { opacity: 0, scale: 0.95 }
              : { opacity: 0, y: 18 };
    const enter =
        from === "x"
            ? { opacity: 1, x: 0 }
            : from === "scale"
              ? { opacity: 1, scale: 1 }
              : { opacity: 1, y: 0 };
    return (
        <AnimatePresence initial={false}>
            {fragmentIndex >= showAt && (
                <motion.div
                    initial={initial}
                    animate={enter}
                    exit={initial}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className={className}
                    data-mascot-avoid="true"
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const Bg = ({ url, overlay = "rgba(255,255,255,0.9)", children }) => (
    <div className="absolute inset-0 overflow-hidden">
        <img
            src={url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
        />
        <div
            className="absolute inset-0"
            style={{ background: overlay }}
        />
        <div className="relative z-10 w-full h-full px-16 py-14 flex flex-col">
            {children}
        </div>
    </div>
);

const Header = ({ kicker, title, color = "#14202a", kickerColor = "#4a5662" }) => (
    <div className="mb-6" data-mascot-avoid="true">
        <p
            className="uppercase tracking-[0.32em] font-semibold mb-3"
            style={{ color: kickerColor, fontSize: 16 }}
        >
            {kicker}
        </p>
        <h2
            className="font-semibold tracking-tight leading-tight"
            style={{ color, fontSize: 56 }}
        >
            {title}
        </h2>
    </div>
);

const Card = ({ children, style = {}, className = "" }) => (
    <div
        className={className}
        data-mascot-avoid="true"
        style={{
            background: "rgba(255,255,255,0.92)",
            borderRadius: 24,
            border: "1px solid rgba(255,255,255,0.7)",
            boxShadow: "0 24px 60px rgba(15,23,42,0.10)",
            backdropFilter: "blur(16px)",
            padding: 28,
            overflow: "hidden",
            ...style,
        }}
    >
        {children}
    </div>
);

// SVG Arc path helper (draws counter-clockwise from startAngle to endAngle)
const getArcPath = (cx, cy, r, startAngle, endAngle) => {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const startX = cx + r * Math.cos(startRad);
    const startY = cy - r * Math.sin(startRad);
    const endX = cx + r * Math.cos(endRad);
    const endY = cy - r * Math.sin(endRad);
    const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
    return `M ${startX},${startY} A ${r},${r} 0 ${largeArc},0 ${endX},${endY}`;
};

/* ============ Slide 1 · Angle Types & Basics ============ */
const AngleTypesBasicsSlide = ({ fragmentIndex }) => {
    const [theta, setTheta] = React.useState(0);

    React.useEffect(() => {
        let target = 0;
        if (fragmentIndex === 1) target = 0;
        else if (fragmentIndex === 2) target = 38;
        else if (fragmentIndex === 3) target = 90;
        else if (fragmentIndex === 4) target = 120;
        else if (fragmentIndex >= 5) target = 180;

        let animationFrame;
        const animate = () => {
            setTheta((prev) => {
                const diff = target - prev;
                if (Math.abs(diff) < 0.2) return target;
                return prev + diff * 0.15; // Smooth easing
            });
            animationFrame = requestAnimationFrame(animate);
        };
        animate();
        return () => cancelAnimationFrame(animationFrame);
    }, [fragmentIndex]);

    const rad = (theta * Math.PI) / 180;
    const cx = 200;
    const cy = 260;
    const rx = cx + 180 * Math.cos(rad);
    const ry = cy - 180 * Math.sin(rad);

    const getClassification = (t) => {
        const rounded = Math.round(t);
        if (rounded === 0) return "Zero";
        if (rounded < 90) return "Acute";
        if (rounded === 90) return "Right";
        if (rounded < 180) return "Obtuse";
        return "Straight";
    };

    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/044172df-b45f-4334-8528-e00ba8c488bb.jpg"
            overlay="rgba(255,255,255,0.92)"
        >
            <Header kicker="Concept · Geometry Basics" title="Angle Types and Basics" />
            <div className="flex-1 grid grid-cols-12 gap-12 items-center">
                {/* Left Card: Definitions */}
                <div className="col-span-6 space-y-4">
                    <Reveal showAt={1} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ padding: "20px 24px" }}>
                            <p style={{ fontSize: 18, color: "#1e293b", lineHeight: 1.4 }}>
                                An <strong>angle</strong> is formed by two rays with a common vertex. Its size measures the turn between them.
                            </p>
                        </Card>
                    </Reveal>

                    <div className="grid grid-cols-2 gap-4">
                        <Reveal showAt={2} fragmentIndex={fragmentIndex} from="x">
                            <Card style={{ padding: 16, background: fragmentIndex === 2 ? "rgba(59,130,246,0.06)" : undefined, border: fragmentIndex === 2 ? "1.5px solid #3b82f6" : undefined }}>
                                <p className="font-semibold text-blue-600" style={{ fontSize: 16 }}>Acute Angle</p>
                                <p style={{ fontSize: 14, color: "#475569", marginTop: 4 }}>
                                    Greater than <InlineMath math="0^\circ" /> and less than <InlineMath math="90^\circ" />.
                                </p>
                            </Card>
                        </Reveal>

                        <Reveal showAt={3} fragmentIndex={fragmentIndex} from="x">
                            <Card style={{ padding: 16, background: fragmentIndex === 3 ? "rgba(16,185,129,0.06)" : undefined, border: fragmentIndex === 3 ? "1.5px solid #10b981" : undefined }}>
                                <p className="font-semibold text-emerald-600" style={{ fontSize: 16 }}>Right Angle</p>
                                <p style={{ fontSize: 14, color: "#475569", marginTop: 4 }}>
                                    Exactly <InlineMath math="90^\circ" /> (marked with a square).
                                </p>
                            </Card>
                        </Reveal>

                        <Reveal showAt={4} fragmentIndex={fragmentIndex} from="x">
                            <Card style={{ padding: 16, background: fragmentIndex === 4 ? "rgba(245,158,11,0.06)" : undefined, border: fragmentIndex === 4 ? "1.5px solid #f59e0b" : undefined }}>
                                <p className="font-semibold text-amber-600" style={{ fontSize: 16 }}>Obtuse Angle</p>
                                <p style={{ fontSize: 14, color: "#475569", marginTop: 4 }}>
                                    Greater than <InlineMath math="90^\circ" /> and less than <InlineMath math="180^\circ" />.
                                </p>
                            </Card>
                        </Reveal>

                        <Reveal showAt={5} fragmentIndex={fragmentIndex} from="x">
                            <Card style={{ padding: 16, background: fragmentIndex === 5 ? "rgba(239,68,68,0.06)" : undefined, border: fragmentIndex === 5 ? "1.5px solid #ef4444" : undefined }}>
                                <p className="font-semibold text-rose-600" style={{ fontSize: 16 }}>Straight Angle</p>
                                <p style={{ fontSize: 14, color: "#475569", marginTop: 4 }}>
                                    Exactly <InlineMath math="180^\circ" /> (forms a flat horizontal line).
                                </p>
                            </Card>
                        </Reveal>
                    </div>

                    <Reveal showAt={6} fragmentIndex={fragmentIndex}>
                        <Card style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.3)" }}>
                            <p className="font-bold text-violet-700 uppercase tracking-widest text-xs mb-1">SAT Tip</p>
                            <p style={{ fontSize: 15, color: "#4c1d95", lineHeight: 1.4 }}>
                                Boundary values matter: <InlineMath math="90^\circ" /> is right, and <InlineMath math="180^\circ" /> is straight. Always use numeric measure over visual appearance!
                            </p>
                        </Card>
                    </Reveal>
                </div>

                {/* Right Column: Interactive Arm Graph */}
                <div className="col-span-6 flex flex-col items-center">
                    <Card style={{ width: 500, height: 500, padding: 24, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <div className="flex justify-between items-center w-full">
                            <span className="text-xs uppercase tracking-wider text-[#475569] font-bold">Angle Visualizer</span>
                            <span className="px-3 py-1 bg-slate-900 text-white rounded-full font-bold text-sm tracking-wide">
                                {Math.round(theta)}° - {getClassification(theta)}
                            </span>
                        </div>

                        <div className="flex-1 flex items-center justify-center">
                            <svg width="400" height="400" className="overflow-visible">


                                {/* Vertex Point */}
                                <circle cx={cx} cy={cy} r="6" fill="#0f172a" />

                                {/* Fixed Base Ray (points along positive x-axis) */}
                                <line x1={cx} y1={cy} x2={cx + 180} y2={cy} stroke="#1e293b" strokeWidth="5.5" strokeLinecap="round" />

                                {/* Rotating Arm Ray */}
                                <line x1={cx} y1={cy} x2={rx} y2={ry} stroke="#1e293b" strokeWidth="5.5" strokeLinecap="round" />

                                {/* Angle Arc or Right Angle Square */}
                                {Math.round(theta) === 90 ? (
                                    <path d={`M ${cx + 30},${cy} L ${cx + 30},${cy - 30} L ${cx},${cy - 30}`} fill="none" stroke="#3b82f6" strokeWidth="3" />
                                ) : (
                                    theta > 0 && (
                                        <path d={getArcPath(cx, cy, 60, 0, theta)} fill="none" stroke="#3b82f6" strokeWidth="3" />
                                    )
                                )}
                            </svg>
                        </div>
                    </Card>
                </div>
            </div>
        </Bg>
    );
};

/* ============ Slide 2 · Core Angle Measure Facts ============ */
const AngleMeasureFactsSlide = ({ fragmentIndex }) => {
    const [angle1, setAngle1] = React.useState(65);
    const [angle2, setAngle2] = React.useState(180);

    React.useEffect(() => {
        let target1 = 65;
        let target2 = 180;
        if (fragmentIndex >= 4) {
            target1 = 120;
            target2 = 215;
        }

        let animationFrame;
        const animate = () => {
            setAngle1((prev) => prev + (target1 - prev) * 0.12);
            setAngle2((prev) => prev + (target2 - prev) * 0.12);
            animationFrame = requestAnimationFrame(animate);
        };
        animate();
        return () => cancelAnimationFrame(animationFrame);
    }, [fragmentIndex]);

    const cx = 250;
    const cy = 250;

    // Ray endpoints
    const r0x = cx + 180;
    const r0y = cy;

    const rad1 = (angle1 * Math.PI) / 180;
    const r1x = cx + 180 * Math.cos(rad1);
    const r1y = cy - 180 * Math.sin(rad1);

    const rad2 = (angle2 * Math.PI) / 180;
    const r2x = cx + 180 * Math.cos(rad2);
    const r2y = cy - 180 * Math.sin(rad2);

    // Left line extension (visible for 180 rule, fades out for 360 rule)
    const is360Mode = fragmentIndex >= 4;

    // Angle label bisectors
    const bisect1 = angle1 / 2;
    const bisect1Rad = (bisect1 * Math.PI) / 180;
    const l1x = cx + 75 * Math.cos(bisect1Rad);
    const l1y = cy - 75 * Math.sin(bisect1Rad);

    const bisect2 = angle1 + (angle2 - angle1) / 2;
    const bisect2Rad = (bisect2 * Math.PI) / 180;
    const l2x = cx + 85 * Math.cos(bisect2Rad);
    const l2y = cy - 85 * Math.sin(bisect2Rad);

    const bisect3 = angle2 + (360 - angle2) / 2;
    const bisect3Rad = (bisect3 * Math.PI) / 180;
    const l3x = cx + 85 * Math.cos(bisect3Rad);
    const l3y = cy - 85 * Math.sin(bisect3Rad);

    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/044172df-b45f-4334-8528-e00ba8c488bb.jpg"
            overlay="rgba(255,255,255,0.92)"
        >
            <Header kicker="Concept · Properties & Rules" title="Core Angle Measure Facts" />
            <div className="flex-1 grid grid-cols-12 gap-12 items-center">
                {/* Left column: Rules info */}
                <div className="col-span-6 space-y-4">
                    <Reveal showAt={1} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ borderLeft: "5px solid #3b82f6", background: fragmentIndex < 3 ? "rgba(59,130,246,0.03)" : undefined }}>
                            <p className="font-semibold text-blue-700 text-lg uppercase tracking-wider mb-1">180° Rule (Straight Line)</p>
                            <p style={{ fontSize: 16, color: "#1e293b", lineHeight: 1.4 }}>
                                Angles along a straight line sum to <InlineMath math="180^\circ" />. If split by a ray, their adjacent measures add to <InlineMath math="180^\circ" />.
                            </p>
                        </Card>
                    </Reveal>

                    <Reveal showAt={2} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ padding: "16px 24px", background: "rgba(248,250,252,0.9)" }}>
                            <p style={{ fontSize: 16, color: "#475569" }}>
                                <strong>Example:</strong> One angle is <InlineMath math="65^\circ" />. The other must be:
                                <br />
                                <span className="text-xl font-bold text-slate-800 block mt-2">
                                    <InlineMath math="180^\circ - 65^\circ = 115^\circ" />
                                </span>
                            </p>
                        </Card>
                    </Reveal>

                    <Reveal showAt={3} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ borderLeft: "5px solid #8b5cf6", background: fragmentIndex >= 3 ? "rgba(139,92,246,0.03)" : undefined }}>
                            <p className="font-semibold text-violet-700 text-lg uppercase tracking-wider mb-1">360° Rule (Full Turn)</p>
                            <p style={{ fontSize: 16, color: "#1e293b", lineHeight: 1.4 }}>
                                Angles that completely wrap around a point sum to <InlineMath math="360^\circ" />.
                            </p>
                        </Card>
                    </Reveal>

                    <Reveal showAt={4} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ padding: "16px 24px", background: "rgba(248,250,252,0.9)" }}>
                            <p style={{ fontSize: 16, color: "#475569" }}>
                                <strong>Example:</strong> Given <InlineMath math="120^\circ" />, <InlineMath math="95^\circ" />, and <InlineMath math="x^\circ" />:
                                <br />
                                <span className="text-xl font-bold text-slate-800 block mt-2">
                                    <InlineMath math="x = 360^\circ - (120^\circ + 95^\circ) = 145^\circ" />
                                </span>
                            </p>
                        </Card>
                    </Reveal>
                </div>

                {/* Right column: Arc Splits Graph */}
                <div className="col-span-6 flex flex-col items-center">
                    {fragmentIndex >= 2 && (
                        <Card style={{ width: 500, height: 500, padding: 24, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                            <span className="text-xs uppercase tracking-wider text-[#475569] font-bold">
                                {is360Mode ? "360° Full Turn Split" : "180° Straight Line Split"}
                            </span>

                            <div className="flex-1 flex items-center justify-center">
                                <svg width="400" height="400" className="overflow-visible">
                                    {/* Grid reference lines */}
                                    <line x1="0" y1="200" x2="400" y2="200" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />
                                    <line x1="200" y1="0" x2="200" y2="400" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />

                                    {/* Straight Line Extension (left line) */}
                                    <line
                                        x1={cx}
                                        y1={cy}
                                        x2={cx - 180}
                                        y2={cy}
                                        stroke="#3b82f6"
                                        strokeWidth="5"
                                        opacity={is360Mode ? 0.15 : 1}
                                        strokeLinecap="round"
                                        style={{ transition: "opacity 0.6s ease" }}
                                    />

                                    {/* Faint complete turn dashed circle */}
                                    <circle cx={cx} cy={cy} r="120" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="4 4" opacity={is360Mode ? 0.5 : 0.15} />

                                    {/* Base Ray (0°) */}
                                    <line x1={cx} y1={cy} x2={r0x} y2={r0y} stroke="#3b82f6" strokeWidth="5.5" strokeLinecap="round" />

                                    {/* Ray 1 (splitting ray) */}
                                    <line x1={cx} y1={cy} x2={r1x} y2={r1y} stroke="#ef4444" strokeWidth="5.5" strokeLinecap="round" />

                                    {/* Ray 2 (left/animated ray) */}
                                    <line x1={cx} y1={cy} x2={r2x} y2={r2y} stroke="#10b981" strokeWidth="5.5" strokeLinecap="round" />

                                    {/* Vertex Point */}
                                    <circle cx={cx} cy={cy} r="5" fill="#0f172a" />

                                    {/* Sector Arcs & Text Labels */}
                                    {!is360Mode ? (
                                        <>
                                            {/* 65° Arc */}
                                            <path d={getArcPath(cx, cy, 55, 0, angle1)} fill="none" stroke="#ef4444" strokeWidth="2.5" />
                                            <text x={l1x} y={l1y} fill="#b91c1c" fontSize="16" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                                {Math.round(angle1)}°
                                            </text>

                                            {/* 115° Arc */}
                                            <path d={getArcPath(cx, cy, 65, angle1, angle2)} fill="none" stroke="#10b981" strokeWidth="2.5" />
                                            <text x={l2x} y={l2y} fill="#047857" fontSize="16" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                                {Math.round(angle2 - angle1)}°
                                            </text>
                                        </>
                                    ) : (
                                        <>
                                            {/* 120° Arc */}
                                            <path d={getArcPath(cx, cy, 55, 0, angle1)} fill="none" stroke="#ef4444" strokeWidth="2.5" />
                                            <text x={l1x} y={l1y} fill="#b91c1c" fontSize="16" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                                {Math.round(angle1)}°
                                            </text>

                                            {/* 95° Arc */}
                                            <path d={getArcPath(cx, cy, 65, angle1, angle2)} fill="none" stroke="#10b981" strokeWidth="2.5" />
                                            <text x={l2x} y={l2y} fill="#047857" fontSize="16" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                                {Math.round(angle2 - angle1)}°
                                            </text>

                                            {/* 145° Arc */}
                                            <path d={getArcPath(cx, cy, 75, angle2, 360)} fill="none" stroke="#8b5cf6" strokeWidth="2.5" />
                                            <text x={l3x} y={l3y} fill="#6d28d9" fontSize="16" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                                {Math.round(360 - angle2)}°
                                            </text>
                                        </>
                                    )}
                                </svg>
                            </div>
                        </Card>
                    )}
                </div>
            </div>
        </Bg>
    );
};

/* ============ Slide 3 · Algebra in Angle Geometry ============ */
const AngleAlgebraSlide = ({ fragmentIndex }) => {
    const cx = 250;
    const cy = 250;

    // Ray coordinates (Fixed split at 60°/120°)
    const rx0 = cx + 180;
    const ry0 = cy;

    const rad1 = (60 * Math.PI) / 180;
    const rx1 = cx + 180 * Math.cos(rad1);
    const ry1 = cy - 180 * Math.sin(rad1);

    const rx2 = cx - 180;
    const ry2 = cy;

    // Label position midpoints
    const l1x = cx + 90 * Math.cos((30 * Math.PI) / 180);
    const l1y = cy - 90 * Math.sin((30 * Math.PI) / 180);

    const l2x = cx + 90 * Math.cos((120 * Math.PI) / 180);
    const l2y = cy - 90 * Math.sin((120 * Math.PI) / 180);

    const showSolved = fragmentIndex >= 4;

    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/044172df-b45f-4334-8528-e00ba8c488bb.jpg"
            overlay="rgba(255,255,255,0.92)"
        >
            <Header kicker="Concept · Algebra Application" title="Algebra in Angle Geometry" />
            <div className="flex-1 grid grid-cols-12 gap-12 items-center">
                {/* Left Column: Split SVG Graph */}
                <div className="col-span-6 flex flex-col items-center">
                    {fragmentIndex >= 2 && (
                        <Card style={{ width: 500, height: 500, padding: 24, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                            <span className="text-xs uppercase tracking-wider text-[#475569] font-bold">Angle Diagram Setup</span>

                            <div className="flex-1 flex items-center justify-center relative w-full h-full">
                                <svg width="400" height="400" className="overflow-visible">
                                    {/* Base Straight Line (y=0) */}
                                    <line x1={rx2} y1={cy} x2={rx0} y2={ry0} stroke="#475569" strokeWidth="5" strokeLinecap="round" />

                                    {/* Splitting Ray at 60° */}
                                    <line x1={cx} y1={cy} x2={rx1} y2={ry1} stroke="#0f172a" strokeWidth="5.5" strokeLinecap="round" />

                                    {/* Vertex Point */}
                                    <circle cx={cx} cy={cy} r="5" fill="#0f172a" />

                                    {/* 60° Sector Arc */}
                                    <path d={getArcPath(cx, cy, 60, 0, 60)} fill="none" stroke="#0284c7" strokeWidth="3" />

                                    {/* 120° Sector Arc */}
                                    <path d={getArcPath(cx, cy, 80, 60, 180)} fill="none" stroke="#ea580c" strokeWidth="3" />
                                </svg>

                                {/* KaTeX Labels overlaying the SVG */}
                                <div className="absolute inset-0 pointer-events-none">
                                    <AnimatePresence mode="wait">
                                        {!showSolved ? (
                                            <motion.div
                                                key="algebraic"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="absolute inset-0"
                                            >
                                                {/* (2x + 10)° Label */}
                                                <div className="absolute text-slate-800 font-semibold" style={{ left: l1x + 20, top: l1y - 30, fontSize: 18 }}>
                                                    <InlineMath math="(2x + 10)^\circ" />
                                                </div>
                                                {/* (4x + 20)° Label */}
                                                <div className="absolute text-slate-800 font-semibold" style={{ left: l2x - 70, top: l2y - 20, fontSize: 18 }}>
                                                    <InlineMath math="(4x + 20)^\circ" />
                                                </div>
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key="solved"
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="absolute inset-0"
                                            >
                                                {/* 60° Label */}
                                                <div className="absolute text-emerald-700 font-bold text-3xl" style={{ left: l1x + 35, top: l1y - 15 }}>
                                                    <InlineMath math="60^\circ" />
                                                </div>
                                                {/* 120° Label */}
                                                <div className="absolute text-emerald-700 font-bold text-3xl" style={{ left: l2x - 45, top: l2y - 15 }}>
                                                    <InlineMath math="120^\circ" />
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </Card>
                    )}
                </div>

                {/* Right Column: Algebra Cards */}
                <div className="col-span-6 space-y-4">
                    <Reveal showAt={1} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ borderLeft: "5px solid #f59e0b" }}>
                            <p className="font-semibold text-amber-700 text-lg uppercase tracking-wider mb-1">Strategy</p>
                            <p style={{ fontSize: 16, color: "#1e293b", lineHeight: 1.4 }}>
                                Unknown angle measures are often expressed as algebraic variables. Setup an equation by translating the diagram using the <InlineMath math="180^\circ" /> straight-line rule.
                            </p>
                        </Card>
                    </Reveal>

                    <Reveal showAt={3} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ padding: 18 }}>
                            <p className="font-semibold text-slate-800 text-base mb-1">Step 1: Setup Equation</p>
                            <p style={{ fontSize: 15, color: "#475569" }}>
                                Add both angles and set their sum to <InlineMath math="180^\circ" />:
                            </p>
                            <div className="text-xl mt-2 font-medium bg-slate-50 p-2 rounded text-center border">
                                <BlockMath math="(2x + 10) + (4x + 20) = 180" />
                            </div>
                        </Card>
                    </Reveal>

                    <Reveal showAt={4} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ padding: 18 }}>
                            <p className="font-semibold text-slate-800 text-base mb-1">Step 2: Solve for x</p>
                            <p style={{ fontSize: 15, color: "#475569" }}>
                                Combine like terms and solve:
                            </p>
                            <div className="text-xl mt-2 font-medium bg-slate-50 p-2 rounded text-center border">
                                <BlockMath math="6x + 30 = 180 \;\Rightarrow\; 6x = 150 \;\Rightarrow\; x = 25" />
                            </div>
                        </Card>
                    </Reveal>

                    <Reveal showAt={5} fragmentIndex={fragmentIndex} from="x">
                        <Card style={{ padding: 18, background: "rgba(16,185,129,0.03)", border: "1.5px solid rgba(16,185,129,0.3)" }}>
                            <p className="font-semibold text-emerald-800 text-base mb-1">Step 3: Substitute &amp; Check</p>
                            <p style={{ fontSize: 15, color: "#065f46" }}>
                                Plug <InlineMath math="x = 25" /> back to find actual measures:
                            </p>
                            <div className="text-lg mt-2 font-medium" style={{ color: "#065f46" }}>
                                <InlineMath math="2(25) + 10 = 60^\circ" />
                                <br />
                                <InlineMath math="4(25) + 20 = 120^\circ" />
                                <br />
                                <span className="font-bold text-emerald-700 block mt-1">
                                    <InlineMath math="60^\circ + 120^\circ = 180^\circ" /> (Works!)
                                </span>
                            </div>
                        </Card>
                    </Reveal>
                </div>
            </div>
        </Bg>
    );
};

/* ============ Slide 4 · Recap Slide ============ */
const RecapSlide = ({ fragmentIndex }) => (
    <Bg
        url="https://asset.sparkl.me/pb/ai-images/img/044172df-b45f-4334-8528-e00ba8c488bb.jpg"
        overlay="linear-gradient(135deg, rgba(15,23,42,0.85), rgba(30,41,59,0.8))"
    >
        <Header kicker="Recap" title="Geometry Wrap-Up" color="#ffffff" kickerColor="rgba(255,255,255,0.7)" />
        <div className="flex-1 flex flex-col justify-center items-center max-w-6xl mx-auto w-full space-y-6">
            <Reveal showAt={0} fragmentIndex={fragmentIndex} from="scale">
                <p className="italic text-slate-200 text-3xl mb-4 text-center">
                    "Angles are all around us — from clock hands to houses!"
                </p>
            </Reveal>

            <div className="grid grid-cols-2 gap-6 w-full">
                <Reveal showAt={1} fragmentIndex={fragmentIndex} from="x">
                    <Card style={{ background: "rgba(96,165,250,0.15)", border: "1px solid rgba(96,165,250,0.3)", padding: 24 }}>
                        <p className="uppercase tracking-widest text-[#93c5fd] font-bold text-xs mb-2">Classifications</p>
                        <p style={{ color: "white", fontSize: 24, fontWeight: 700 }} className="mb-2">Acute, Right, Obtuse, Straight</p>
                        <p style={{ color: "#cbd5e1", fontSize: 15, lineHeight: 1.4 }}>
                            Acute is less than 90°, Right is exactly 90°, Obtuse is between 90° and 180°, and Straight is exactly 180°.
                        </p>
                    </Card>
                </Reveal>

                <Reveal showAt={2} fragmentIndex={fragmentIndex} from="x">
                    <Card style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)", padding: 24 }}>
                        <p className="uppercase tracking-widest text-[#a7f3d0] font-bold text-xs mb-2">Fundamental Rules</p>
                        <p style={{ color: "white", fontSize: 24, fontWeight: 700 }} className="mb-2">180° and 360° Rules</p>
                        <p style={{ color: "#cbd5e1", fontSize: 15, lineHeight: 1.4 }}>
                            Angles on a straight line sum to 180° (180° Rule). Angles completing a full rotation around a point sum to 360° (360° Rule).
                        </p>
                    </Card>
                </Reveal>

                <Reveal showAt={3} fragmentIndex={fragmentIndex} from="x">
                    <Card style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)", padding: 24, gridColumn: "span 2" }}>
                        <p className="uppercase tracking-widest text-[#fde047] font-bold text-xs mb-2">Algebra Bridge</p>
                        <p style={{ color: "white", fontSize: 24, fontWeight: 700 }} className="mb-2">Solving Unknown Angles</p>
                        <p style={{ color: "#cbd5e1", fontSize: 15, lineHeight: 1.4 }}>
                            Use geometric rules to construct algebraic equations, combine like terms, solve for the variable, and substitute back to verify the actual angle measures.
                        </p>
                    </Card>
                </Reveal>
            </div>
        </div>
    </Bg>
);

/* ============ dispatcher ============ */
export const isAngleRichSlide = (slideId) =>
    ["angle-types-basics", "angle-measure-facts", "angle-algebra", "angle-recap"].includes(slideId);

const AngleSlide = ({ slide, fragmentIndex, activeQuestion }) => {
    const inner = (() => {
        switch (slide?.id) {
            case "angle-types-basics":
                return <AngleTypesBasicsSlide fragmentIndex={fragmentIndex} />;
            case "angle-measure-facts":
                return <AngleMeasureFactsSlide fragmentIndex={fragmentIndex} />;
            case "angle-algebra":
                return <AngleAlgebraSlide fragmentIndex={fragmentIndex} />;
            case "angle-recap":
                return <RecapSlide fragmentIndex={fragmentIndex} />;
            default:
                return null;
        }
    })();

    return (
        <>
            {inner}
            <QuestionPopup
                activeQuestion={activeQuestion}
                slideId={slide?.id}
                fragmentIndex={fragmentIndex}
            />
        </>
    );
};

export default AngleSlide;
